import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';
import { Page, Panel, StatTiles } from '../../components/shell/Page';
import DataGrid, { StatusBadge } from '../../components/datagrid/DataGrid';
import { toast } from 'sonner';
import { Building2, TrendingUp, Users, Target, ExternalLink, BarChart3 } from 'lucide-react';

export default function SuperAdminCRMOverview() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantLeads, setTenantLeads] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/crm/tenants/overview');
      setOverview(res.data || []);
    } catch {
      toast.error('Failed to load CRM overview');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  const totals = overview.reduce(
    (acc, t) => ({
      total: acc.total + (t.total_leads || 0),
      converted: acc.converted + (t.converted || 0),
      newLeads: acc.newLeads + (t.new_leads || 0),
    }),
    { total: 0, converted: 0, newLeads: 0 }
  );
  const overallRate = totals.total > 0 ? Math.round((totals.converted / totals.total) * 1000) / 10 : 0;

  const loadTenantLeads = async (tenantId) => {
    setSelectedTenant(tenantId);
    try {
      // For super admin, filter leads via analytics endpoint
      const res = await api.get(`/crm/analytics?tenant_id=${tenantId}`);
      setTenantLeads(res.data.recent_leads || []);
    } catch {
      toast.error('Failed to load tenant leads');
    }
  };

  const copyEnquiryLink = (t) => {
    const leadFormUrl = `${window.location.origin}/tenant/${t.subdomain}/enquire`;
    navigator.clipboard.writeText(leadFormUrl);
    toast.success('Link copied!');
  };

  return (
    <DashboardLayout>
      <Page data-testid="super-admin-crm-overview">
        <StatTiles items={[
          { label: 'Active tenants', value: overview.length, icon: Building2, iconClass: 'bg-sky-100 text-sky-700' },
          { label: 'Total leads', value: totals.total, icon: Users, iconClass: 'bg-violet-100 text-violet-700' },
          { label: 'New (pending)', value: totals.newLeads, icon: Target, iconClass: 'bg-amber-100 text-amber-700' },
          { label: 'Avg conversion', value: `${overallRate}%`, icon: TrendingUp, iconClass: 'bg-emerald-100 text-emerald-700' },
        ]} />

        <DataGrid
          title="CRM overview — all tenants"
          rows={overview}
          rowKey="tenant_id"
          loading={loading}
          onRefresh={load}
          columns={[
            {
              key: 'tenant_name', header: 'Tenant',
              render: (t) => <span className="font-medium" data-testid={`tenant-row-${t.subdomain}`}>{t.tenant_name}</span>,
            },
            { key: 'subdomain', header: 'Subdomain', className: 'font-mono text-xs text-muted-foreground' },
            { key: 'total_leads', header: 'Total leads', width: 120, align: 'right', accessor: (t) => t.total_leads || 0, className: 'tabular-nums' },
            { key: 'new_leads', header: 'New', width: 100, align: 'right', accessor: (t) => t.new_leads || 0, className: 'tabular-nums' },
            { key: 'converted', header: 'Converted', width: 110, align: 'right', accessor: (t) => t.converted || 0, className: 'tabular-nums' },
            {
              key: 'conversion_rate', header: 'Conversion rate', width: 140, align: 'right',
              accessor: (t) => t.conversion_rate || 0,
              render: (t) => <span className="tabular-nums">{t.conversion_rate || 0}%</span>,
            },
          ]}
          onRowDoubleClick={(t) => loadTenantLeads(t.tenant_id)}
          selectionActions={[
            { key: 'analytics', label: 'View analytics', icon: BarChart3, onClick: ([t]) => loadTenantLeads(t.tenant_id) },
            { key: 'copy-link', label: 'Copy enquiry link', icon: ExternalLink, onClick: ([t]) => copyEnquiryLink(t) },
          ]}
          emptyMessage="No tenants found"
        />

        {/* Recent leads for selected tenant */}
        {selectedTenant && tenantLeads.length > 0 && (
          <Panel title={`Recent Leads — ${overview.find(t => t.tenant_id === selectedTenant)?.tenant_name || ''}`}>
            <div className="space-y-2">
              {tenantLeads.map(lead => (
                <div key={lead.id} className="flex items-center justify-between p-2 border rounded text-sm">
                  <div>
                    <p className="font-medium">{lead.first_name} {lead.last_name}</p>
                    <p className="text-xs text-muted-foreground">{lead.email || lead.phone} · {lead.source}</p>
                  </div>
                  <StatusBadge value={lead.stage} />
                </div>
              ))}
            </div>
          </Panel>
        )}
      </Page>
    </DashboardLayout>
  );
}
